import { useEffect, useState } from "react";

import {
	ActionIcon,
	Alert,
	Button,
	Checkbox,
	Group,
	Modal,
	Stack,
	Table,
	Text,
	TextInput,
} from "@mantine/core";
import {
	IconAlertCircle,
	IconDeviceFloppy,
	IconPlus,
	IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/Components/Auth/AuthProvider";
import { confirm } from "@/Components/Utils/confirm";
import { notifyError, notifySuccess } from "@/Components/Utils/notifications";
import { useApiHelper } from "@/contexts";
import type { User, UserGroup } from "@/types/user";

interface GroupMember {
	user: User;
	isOwner: boolean;
}

interface ManageGroupModalProps {
	opened: boolean;
	onClose: () => void;
	group: UserGroup | null;
	isEdit: boolean;
	onGroupSaved: () => void;
}

// Note: isPublic is carried through the DTOs but has no form control here.
export default function ManageGroupModal({
	opened,
	onClose,
	group,
	isEdit,
	onGroupSaved,
}: ManageGroupModalProps) {
	const { t } = useTranslation();
	const apiHelper = useApiHelper();
	const { user: currentUser } = useAuth();

	const [groupName, setGroupName] = useState("");
	const [isPublic, setIsPublic] = useState(false);
	const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
	const [creatorUuid, setCreatorUuid] = useState<string | null>(null);
	const [searchEmail, setSearchEmail] = useState("");
	const [searchError, setSearchError] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// Hydrate on open: edit mode maps users+owners (deduped); create mode
	// resets and seeds the creator as locked owner.
	useEffect(() => {
		if (!opened) {
			return;
		}
		if (group) {
			setGroupName(group.name);
			setIsPublic(group.isPublic);
			if (currentUser) {
				setCreatorUuid(currentUser.user_uuid || currentUser.sub);
			}
			const members = new Map<string, GroupMember>();
			group.users?.forEach((user) => {
				members.set(user.uuid, { user, isOwner: false });
			});
			group.owners?.forEach((owner) => {
				const existing = members.get(owner.uuid);
				if (existing) {
					existing.isOwner = true;
				} else {
					members.set(owner.uuid, { user: owner, isOwner: true });
				}
			});
			setGroupMembers(Array.from(members.values()));
		} else {
			setGroupName("");
			setIsPublic(false);
			setSearchEmail("");
			setSearchError("");
			if (!currentUser) {
				setGroupMembers([]);
				setCreatorUuid(null);
				return;
			}
			const realUserUuid = currentUser.user_uuid || currentUser.sub;
			setCreatorUuid(realUserUuid);
			setGroupMembers([
				{
					user: {
						uuid: realUserUuid,
						sub: realUserUuid,
						email: currentUser.email,
						firstname: currentUser.firstname,
						lastname: currentUser.lastname,
						password: "",
						createdAt:
							currentUser.createdAt || new Date().toISOString(),
						lastLogin: currentUser.lastLogin,
						groups: [],
					},
					isOwner: true,
				},
			]);
		}
	}, [opened, group, currentUser]);

	const isCreator = (uuid: string) => uuid === creatorUuid;

	const addUserByEmail = async () => {
		if (!searchEmail.trim()) return;
		setSearchError("");
		setIsSearching(true);
		try {
			const user = await apiHelper.users.searchUserByEmail(
				searchEmail.trim()
			);
			if (!user) {
				setSearchError(t("No user found with this email"));
				return;
			}
			if (groupMembers.some((m) => m.user.uuid === user.uuid)) {
				setSearchError(t("This user is already in the group"));
				return;
			}
			setGroupMembers((prev) => [...prev, { user, isOwner: false }]);
			setSearchEmail("");
		} catch {
			setSearchError(t("Error while searching for the user"));
		} finally {
			setIsSearching(false);
		}
	};

	const removeMember = (uuid: string) => {
		if (isCreator(uuid)) return;
		setGroupMembers((prev) => prev.filter((m) => m.user.uuid !== uuid));
	};

	const toggleOwner = (uuid: string) => {
		if (isCreator(uuid)) return;
		setGroupMembers((prev) =>
			prev.map((m) =>
				m.user.uuid === uuid ? { ...m, isOwner: !m.isOwner } : m
			)
		);
	};

	const canSave = !isEdit
		? groupName.trim().length > 0
		: groupName.trim().length > 0 &&
			groupMembers.length > 0 &&
			groupMembers.some((m) => m.isOwner);

	const isCurrentUserOwner = (() => {
		if (!group) return false;
		const currentUserUuid = currentUser?.user_uuid || currentUser?.sub;
		return groupMembers.some(
			(m) =>
				m.isOwner &&
				(m.user.uuid === currentUserUuid ||
					m.user.sub === currentUserUuid)
		);
	})();

	const saveGroup = async () => {
		if (!canSave) return;
		setIsSaving(true);
		try {
			if (isEdit && group) {
				await apiHelper.users.updateUserGroup(group.uuid, {
					name: groupName.trim(),
					isPublic,
				});
				const users = groupMembers.map((m) => m.user);
				await apiHelper.users.editGroupUsers(group.uuid, users);
				const ownerUuids = groupMembers
					.filter((m) => m.isOwner)
					.map((m) => m.user.uuid);
				await apiHelper.users.updateGroupOwners(group.uuid, ownerUuids);
			} else {
				await apiHelper.users.addUserGroup({
					name: groupName.trim(),
					isPublic,
					userUuids: groupMembers.map((m) => m.user.uuid),
					ownerUuids: groupMembers
						.filter((m) => m.isOwner)
						.map((m) => m.user.uuid),
				});
			}
			onClose();
			onGroupSaved();
		} catch (error) {
			console.error("Error saving group:", error);
			notifyError(t("Error"), t("Error while saving the group"));
		} finally {
			setIsSaving(false);
		}
	};

	const deleteGroup = async () => {
		if (!group) return;
		const confirmed = await confirm({
			title: t("Delete the group"),
			message: t(
				'Are you sure you want to delete the group "{{name}}"?\nThis action is irreversible.',
				{ name: group.name }
			),
			type: "danger",
			confirmLabel: t("Delete"),
			cancelLabel: t("cancel").capfirst(),
		});
		if (!confirmed) return;

		setIsSaving(true);
		try {
			await apiHelper.users.deleteUserGroup(group.uuid);
			onClose();
			onGroupSaved();
			notifySuccess(t("Success"), t("Group deleted successfully"));
		} catch (error) {
			console.error("Error deleting group:", error);
			const message = (
				error as { response?: { data?: { message?: string } } }
			)?.response?.data?.message;
			notifyError(
				t("Error"),
				message || t("Error while deleting the group")
			);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			size={800}
			title={isEdit ? t("Edit the group") : t("Create a group")}
		>
			<Stack gap="md">
				<TextInput
					label={
						<>
							{t("Group name")}{" "}
							<Text span c="danger">
								*
							</Text>
						</>
					}
					placeholder={t("Enter the group name")}
					required
					value={groupName}
					onChange={(e) => setGroupName(e.currentTarget.value)}
				/>

				<div>
					<Group gap="xs" align="flex-end" wrap="nowrap">
						<TextInput
							style={{ flex: 1 }}
							type="email"
							label={
								<>
									{t("Add members")}{" "}
									{isEdit && (
										<Text span c="danger">
											*
										</Text>
									)}
								</>
							}
							placeholder={t("Enter a user's email")}
							value={searchEmail}
							disabled={isSearching}
							onChange={(e) =>
								setSearchEmail(e.currentTarget.value)
							}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									addUserByEmail();
								}
							}}
						/>
						<Button
							leftSection={<IconPlus size={16} />}
							loading={isSearching}
							disabled={!searchEmail.trim()}
							onClick={addUserByEmail}
						>
							{t("Add")}
						</Button>
					</Group>
					{searchError && (
						<Text fz="xs" c="danger" mt={4}>
							{searchError}
						</Text>
					)}
					{!isEdit && (
						<Text fz="xs" c="dimmed" mt={4}>
							{t(
								"You will automatically be added as a member and owner of the group"
							)}
						</Text>
					)}
				</div>

				{groupMembers.length > 0 && (
					<div>
						<Text fz="sm" fw={500} mb={4}>
							{t("Group members")} ({groupMembers.length})
						</Text>
						<Table>
							<Table.Thead>
								<Table.Tr>
									<Table.Th>{t("Name")}</Table.Th>
									<Table.Th>{t("Email")}</Table.Th>
									<Table.Th ta="center">
										{t("Owner")}
									</Table.Th>
									<Table.Th />
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{groupMembers.map((member) => (
									<Table.Tr key={member.user.uuid}>
										<Table.Td>
											{member.user.firstname}{" "}
											{member.user.lastname}
										</Table.Td>
										<Table.Td>{member.user.email}</Table.Td>
										<Table.Td ta="center">
											<Checkbox
												display="inline-block"
												aria-label={t("Owner")}
												checked={member.isOwner}
												disabled={isCreator(
													member.user.uuid
												)}
												onChange={() =>
													toggleOwner(
														member.user.uuid
													)
												}
											/>
										</Table.Td>
										<Table.Td ta="right">
											<ActionIcon
												aria-label={t("Remove")}
												disabled={isCreator(
													member.user.uuid
												)}
												onClick={() =>
													removeMember(
														member.user.uuid
													)
												}
											>
												<IconTrash size={16} />
											</ActionIcon>
										</Table.Td>
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
						<Text fz="xs" c="dimmed" mt={4}>
							{t("At least one member must be an owner.")}{" "}
							{!isEdit &&
								t(
									"The group creator cannot be removed or lose their owner rights."
								)}
						</Text>
					</div>
				)}

				{isEdit && !canSave && groupMembers.length > 0 && (
					<Alert color="warning" icon={<IconAlertCircle size={16} />}>
						{t("You must set at least one owner for the group")}
					</Alert>
				)}

				<Group justify="space-between" mt="sm">
					<div>
						{isEdit && group && isCurrentUserOwner && (
							<Button
								color="danger"
								leftSection={<IconTrash size={16} />}
								disabled={isSaving}
								onClick={deleteGroup}
							>
								{t("Delete the group")}
							</Button>
						)}
					</div>
					<Group gap="sm">
						<Button
							variant="default"
							disabled={isSaving}
							onClick={onClose}
						>
							{t("cancel").capfirst()}
						</Button>
						<Button
							leftSection={<IconDeviceFloppy size={16} />}
							loading={isSaving}
							disabled={!canSave}
							onClick={saveGroup}
						>
							{isEdit ? t("Update") : t("Create the group")}
						</Button>
					</Group>
				</Group>
			</Stack>
		</Modal>
	);
}
